import React from 'react';
import { Feature, ColorFrom } from '@tencent/workbench';
import { NumberInputFrom, ColorOperations } from '@tencent/wb-ext-form';
import BaseDrawerEffectFeature from '@framework/workbench/common/feature/base-drawer-effect-feature';
import { EffectPropsField } from '@editor/src/js/constants';
import GlowPresetMenu from '@framework/frame-pc/components/palette-panel/effect-preset-menu/glow-preset-menu/index-text';
import { FormItemName, INPUT_PROPS_CALLBACK_KEY } from '@framework/workbench/drawer/constants';
import PresetPanel from '@framework/workbench/drawer/components/preset-panel';
import type { IGlowFeature } from './glow-interface';
import { ActionType } from '@editor/src/js/constants/common-action-type';
import { IGlowModelVal } from '@src/slide/domain/ui-model/element-modeles/property/effect/effect.interface';
import _isEqualWith from 'lodash/isEqualWith';
import { MULTI } from '@src/js/constant';
import { ColorSelectButton } from '@framework/workbench/drawer/components/color-select-button';
import {
    TDWVer5Type,
    TDWVer6Type,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { ITDColor, TDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';
import { handleInputBlurInitAndReset } from '@src/slide/application/service/drawer-input-manager/drawer-handler';

const defaultGlowProps = {
    [EffectPropsField.COLOR.name]: '#0E0E0E',
    [EffectPropsField.OPACITY.name]: 0,
    [EffectPropsField.SIZE.name]: 0,
    tdColor: new TDColor({ rgbaStr: '#0E0E0E' }),
};

export const GLOW_EMPTY = 'glow-empty';
const DEFAULT_EMPTY_COLOR = '#000000';

@Feature()
export class GlowFeature extends BaseDrawerEffectFeature implements IGlowFeature {
    public id = ActionType.FONT_EFFECT;

    // 侧边栏展示数据
    public glowInfoInDrawer: any = { ...defaultGlowProps };

    // 预览时的临时数据
    private glowInfoInPreview;

    // 获取uiModel上的数据
    public get glowInfoInModel() {
        const data = this.getFontEffect(ActionType.GLOW);
        return (data === MULTI ? null : data) ?? defaultGlowProps;
    }

    // 是否是部分设置了效果或者不同部分设置了不同的效果
    public get isMultiDifferent(): boolean {
        return this.isTextMultiDiffValue(ActionType.GLOW);
    }

    // 获取侧边栏透明度数据（预览态有值时说明处于预览态，优先预览态数据）
    public get opacity(): number {
        return Math.round(Number((this.glowInfoInPreview || this.glowInfoInDrawer).opacity)) || 0;
    }

    // 获取侧边栏大小数据（预览态有值时说明处于预览态，优先预览态数据）
    public get size(): number {
        return Number((this.glowInfoInPreview || this.glowInfoInDrawer).size);
    }

    public constructor() {
        super();
        this.addInitPropsCallback(INPUT_PROPS_CALLBACK_KEY.TEXT_GLOW);
    }

    public initPropsCallback = (): void => {
        // 点击文字时，侧边栏同步大小透明度等数据
        if (!this.getTextEditorStateService().isTextPreview) {
            this.glowInfoInPreview = undefined;
            this.glowInfoInDrawer = { ...this.glowInfoInModel };
        }
    };

    // 类型切换面板
    public renderPreset(): any {
        // 表格切换单元格的数值更新，可以靠slide-adaptor触发的onSelectionChange联动触发
        this.initPropsCallback();
        const { iconSrc, iconName } = this.getPresetStyle(this.glowInfoInModel);
        // 当前预览icon
        return (<PresetPanel
            src={iconSrc}
            hasEffect={iconName !== GLOW_EMPTY}
            isMultiDifferent={this.isMultiDifferent}
            onVisibleChange={this.handlePresetVisibleChange}
            disabled={this.isMultiModel() && this.isTable()}
        >
            {/* 详细菜单 */}
            {
                this.getPresetMenuContent()
            }
        </PresetPanel>);
    }

    public getPresetMenuContent = (props?: { style: React.CSSProperties }) => {
        this.initPropsCallback();
        const { iconName } = this.getPresetStyle(this.glowInfoInModel);
        return <GlowPresetMenu
            style={props?.style}
            id={' '} preset={{ style: iconName }}
            onGlowPresetStyleClick={glow => this.handleGlowPresetStyleClick(glow)}
            onGlowPresetStyleMouseEnter={glow => this.handleGlowPresetStyleEnter(glow)}
            onGlowPresetStyleMouseLeave={() => this.handleGlowPresetStyleMouseLeave()}
        />;
    };

    /**
     * 阴影各项指标编辑
     * @param value 指标值
     * @param isPreview 是否是预览（比如拖拽过程中）
     * @param from 编辑来源
     */
    @handleInputBlurInitAndReset(INPUT_PROPS_CALLBACK_KEY.TEXT_GLOW)
    public handleGlowChange(
        value: Partial<IGlowModelVal>,
        dragEnd?: boolean,
        from?: NumberInputFrom,
    ): void {
        const isPreview = this.isInputOnlyChangeUIWithDrag(dragEnd);
        if (isPreview) {
            // 拖拽滑动过程中预览
            this.glowInfoInPreview = { ...this.glowInfoInModel, ...value };
            // 因为文字效果实时更新太卡，所以拖拽过程中不渲染效果
            return;
        }
        // 点击、输入、滑动停止
        this.glowInfoInDrawer = { ...this.glowInfoInModel, ...value };

        // 更新当前预览状态
        this.updatePreviewState(isPreview);
        // 更新文字ui，非预览态则还要上传sdk
        this.updateUiAndSdk();
        this.handleGlowReport(value, from);
    }

    /**
     * 颜色选则面板对应的各种操作响应
     * @param value
     * @param from
     * @param mouseMoveType
     */
    public handleColorPanelOperate = (tdColor: ITDColor, from: ColorFrom) => {
        const options: Partial<IGlowModelVal> = this.getColorOptions(tdColor.srgb_color);
        options.tdColor = tdColor;
        this.handleColorChange(options, from);
        this.getColorPickerManager()?.colorPickerTdwReport({
            color: tdColor.srgb_color,
            colorPickerFrom: TDWVer6Type.COLOR_PICKER_DRAWER,
            colorGroupFrom: from,
            actionType: TDWVer5Type.COLOR_PICKER_GLOW,
        });
    };

    // 颜色编辑
    public handleColorChange = (
        value: Partial<IGlowModelVal>,
        from: ColorFrom | number,
    ): void => {
        this.updatePreviewState(false);
        this.glowInfoInDrawer = { ...this.glowInfoInModel, ...value };
        this.updateUiAndSdk();
    };

    public getColorSelectButton() {
        return <ColorSelectButton
            onColorHover={this.handleColorEnter}
            onMouseLeave={this.handlePaletteLeave}
            onColorChange={this.handleColorPanelOperate}
            value={this.glowInfoInModel?.tdColor || new TDColor({
                rgbaStr: DEFAULT_EMPTY_COLOR,
            })}
            paletteButtonsStyle={{
                marginLeft: 'auto',
                boxSizing: 'border-box',
            }}
        />;
    }

    // 从‘预设’面板切换不同的类型
    private handleGlowPresetStyleClick = (glow: IGlowModelVal): void => {
        this.updatePreviewState(false);
        this.glowInfoInDrawer = { ...defaultGlowProps, ...glow };
        this.updateUiAndSdk();
        this.tdwDrawerReport(FormItemName.FONT_GLOW, { ver5: glow ? 2 : 1 });
    };

    // hover进入不同的类型
    private handleGlowPresetStyleEnter = (glow: IGlowModelVal): void => {
        this.updatePreviewState(true);
        this.glowInfoInPreview = { ...defaultGlowProps, ...glow };
        this.updateUiAndSdk();
    };

    // hover离开
    private handleGlowPresetStyleMouseLeave = (): void => {
        this.glowInfoInPreview = { ...this.glowInfoInDrawer };
        if (this.getTextEditorStateService().isTextPreview) {
            this.callbackByActionType(ActionType.FONT_REFRESH);
            this.updatePreviewState(false);
        }
    };

    // 类型切换面板打开时同步文本数据
    private handlePresetVisibleChange = (visible: boolean): void => {
        if (visible) {
            this.glowInfoInDrawer = { ...this.glowInfoInModel };
        }
    };

    // hover 到颜色上时预览属性
    private handleColorEnter = (color): void => {
        this.updatePreviewState(true);
        this.glowInfoInPreview = { ...this.glowInfoInDrawer, color };
        this.updateUiAndSdk();
    };

    // 离开颜色面板
    private handlePaletteLeave = (): void => {
        this.glowInfoInPreview = { ...this.glowInfoInDrawer };
        this.updateUiAndSdk();
        this.updatePreviewState(false);
    };

    // 更新文字ui， 当处于非预览态时会同时上传sdk（是否处于预览态靠editor更新的获取store状态判断）
    private updateUiAndSdk = (): void => {
        const data = this.getTextEditorStateService().isTextPreview
            ? { ...this.glowInfoInPreview } : { ...this.glowInfoInDrawer };
        const values = { [ActionType.EFFECT]: {} };
        if (data.opacity) {
            data.tdColor?.updateOpacity(data.opacity);
        }
        // 选择的是无效果，直接干掉这个属性，确保数据干净，有利于对比
        if (data && !_isEqualWith(data, defaultGlowProps)) {
            values[ActionType.EFFECT][ActionType.GLOW] = data;
        } else {
            values[ActionType.EFFECT][ActionType.GLOW] = null;
        }
        this.updateProps(this.id, values);
    };


    // 更新全局文本预览状态（如果是退出预览态则同时清空预览数据）
    private updatePreviewState = (isPreview: boolean): void => {
        this.getTextEditorStateService().isTextPreview = isPreview;
        if (!isPreview) {
            this.glowInfoInPreview = undefined;
        }
    };

    private handleGlowReport = (value, from?: NumberInputFrom): void => {
        let reportId;
        let ver6;
        if (value?.size) {
            reportId  = FormItemName.FONT_GLOW_SIZE;
            ver6 = value.size;
        }
        if (value?.opacity) {
            reportId  = FormItemName.FONT_GLOW_OPACITY;
            ver6 = value.opacity;
        }
        this.tdwDrawerReportNumberInput(reportId, { ver6 }, from);
    };

    private getPresetStyle = (glowProps) => {
        const { color } = glowProps;
        const iconNameMap = {
            '#000000': 'glow-1',
            '#F64848': 'glow-2',
            '#FFE59A': 'glow-3',
            '#FDE525': 'glow-4',
            '#B2E922': 'glow-5',
            '#D041E1': 'glow-6',
            '#A98DFD': 'glow-7',
            '#547CF8': 'glow-8',
            '#03C3FF': 'glow-9',
        };
        const iconName = iconNameMap[(color ?? '').toUpperCase()] ?? GLOW_EMPTY;
        const iconSrc = GlowPresetMenu.PresetIconOnShow[iconName];
        return { iconName, iconSrc };
    };
}
